<template>
   <div>
      <div v-if="!loaded">
         <SpinnerOverlay />
      </div>
      <b-container class="client-dashboard">
         <PageTitle title="Welcome back!" />
         <b-row class="mb-4">
            <b-col xl="6" cols="12" class="progress-card-wrapper">
               <b-card id="progress-card" class="info-card h-md-100 mb-4 mb-md-0">
                  <div class="d-flex flex-column continue-section-wrapper">
                     <h3 class="mb-3 card-title bold">Your Progress</h3>
                     <span id="progress-percentage" class="progress-percentage">
                        {{ totalProgress.total }}% Complete
                        {{ openStudies.length > 1 ? `for ${openStudies.length} open studies` : '' }}
                     </span>
                     <b-progress
                        :value="totalProgress.total"
                        :max="100"
                        variant="white"
                        class="progress-white mb-2"
                     ></b-progress>

                     <div v-if="openStudies.length > 1" class="study-prog-container">
                        <div
                           v-for="(study, idx) in openStudies"
                           :key="`study-prog-${study.id}`"
                           class="study-prog"
                        >
                           <span
                              :id="`progress-percentage-${idx + 1}`"
                              class="mr-2 progress-percentage-study"
                           >
                              {{ study.label }} - {{ studyProgress(study.id).total }}%
                           </span>
                           <b-progress
                              :value="studyProgress(study.id).total"
                              :max="100"
                              variant="white"
                              class="progress-white mb-2"
                           ></b-progress>
                        </div>
                     </div>
                  </div>
               </b-card>
            </b-col>
            <b-col xl="6" cols="12" class="progress-card-wrapper">
               <b-card class="info-card h-md-100 mb-4 mb-md-0">
                  <div class="d-flex flex-column continue-section-wrapper">
                     <h4 class="mb-3 card-title bold">
                        {{ continueCardTitle }}
                     </h4>
                     <div class="d-flex justify-content-between align-items-center column-gap">
                        <p v-if="nextAction" class="continue-section-subtitle">
                           {{ nextAction.text }}
                        </p>
                        <p v-else-if="totalProgress < 100" class="continue-section-subtitle">
                           Thank you, all your work is complete. We will notify you when your
                           attention is needed again.
                        </p>
                        <p v-else class="continue-section-subtitle">
                           Thank you. We will review your information and contact you when your
                           credit is ready, or if there are any questions.
                        </p>
                        <div style="display: flex; flex-direction: row; gap: 5px">
                           <b-button
                              id="btn-continue"
                              class="continue-button"
                              v-if="nextAction"
                              variant="success"
                              :to="toAction(nextAction.to, nextAction.studyId)"
                           >
                              Continue
                           </b-button>
                        </div>
                     </div>
                  </div>
               </b-card>
            </b-col>
         </b-row>

         <h2 class="section-title">Progress Overview</h2>

         <div class="d-flex flex-column progress-section-wrapper">
            <!-- EMPLOYEE TIME CARD -->
            <SmeDashboardCard
               v-if="hasTimeSurveyAssignment"
               ident="time-survey"
               title="Employee Time"
               :progress="progress.timeSurvey"
               :action-button-link="{name: 'employees-table'}"
            >
            </SmeDashboardCard>

            <!-- CONTRACTOR CARD -->
            <SmeDashboardCard
               v-if="hasContractorAssignment"
               ident="contractor"
               title="Contract Research"
               :progress="progress.contractors"
               :action-button-link="{name: 'contractors-table'}"
               :action-button-visible="isSME && hasContractorAssignment"
            >
            </SmeDashboardCard>

            <!-- SUPPLIES CARD -->
            <SmeDashboardCard
               v-if="hasSuppliesAssignment"
               ident="supplies"
               title="Supplies"
               :progress="progress.supplies"
               :action-button-link="{name: 'supplies-table'}"
               :action-button-visible="!suppliesComplete || hasSuppliesAssignment"
            >
            </SmeDashboardCard>

            <!-- CLOUD COMPUTING CARD -->
            <SmeDashboardCard
               v-if="hasCloudAssignment"
               ident="cloud-computing"
               title="Cloud Computing"
               :progress="progress.cloud"
               :action-button-link="{name: 'cloud-table'}"
               :action-button-visible="!cloudComplete || hasCloudAssignment"
            >
            </SmeDashboardCard>

            <!-- COMPANY CARD -->
            <SmeDashboardCard
               v-if="hasCompanyQuestionnaireAssignment"
               ident="company-questionnaire"
               title="Company Questionnaire"
               :progress="progress.company"
               :action-button-link="{name: 'questionnaires'}"
               :action-button-visible="!companyComplete"
            ></SmeDashboardCard>

            <!-- PROJECT CARD -->
            <SmeDashboardCard
               v-if="hasProjectQuestionnaireAssignment"
               ident="project-questionnaire"
               title="Project Questionnaire"
               :progress="progress.project"
               :action-button-link="{name: 'questionnaires'}"
               :action-button-visible="!projectComplete"
            ></SmeDashboardCard>

            <!-- PROFILE CARD -->
            <SmeDashboardCard
               ident="profile"
               title="Profile"
               :progress="progress.profile"
               :action-button-link="{name: 'profile', query: {section: 'about'}}"
            ></SmeDashboardCard>
         </div>
      </b-container>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import SmeDashboardCard from './widgets/SmeDashboardCard.vue';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay';

export default {
   components: {SmeDashboardCard, SpinnerOverlay},
   data() {
      return {
         loaded: false,
      };
   },
   computed: {
      ...mapGetters({
         isSME: 'isSME',
         profile: 'profile',
         activeStudy: 'companies/activeStudy',
         activeStudyId: 'companies/activeStudyId',
         openStudies: 'companies/openStudies',
         progress: 'progress/activeStudyProgress',
         totalProgress: 'progress/totalProgress',
         studyProgress: 'progress/studyProgress',
         priorityActionItems: 'progress/priorityActionItems',
         hasCompanyQuestionnaireAssignment: 'progress/hasCompanyQuestionnaireAssignment',
         hasProjectQuestionnaireAssignment: 'progress/hasProjectQuestionnaireAssignment',
         hasTimeSurveyAssignment: 'progress/hasTimeSurveyAssignment',
         hasCloudAssignment: 'progress/hasCloudAssignment',
         hasContractorAssignment: 'progress/hasContractorAssignment',
         hasSuppliesAssignment: 'progress/hasSuppliesAssignment',
         isActiveStudyInSetupStage: 'companies/isActiveStudyInSetupStage',
      }),

      companyId() {
         return this.profile.companyId;
      },

      continueCardTitle() {
         if (this.progress.total === 0) {
            return "Let's get started!";
         } else if (this.nextAction) {
            return 'Continue where you left off';
         } else {
            return 'All done!';
         }
      },

      attentionItems() {
         // First, keep only items for the active study
         let priorityActionItemsCopy = this.priorityActionItems.filter(
            (item) => item.studyId == this.activeStudyId
         );

         // If supplies is null or 100, remove SUPPLIES_DATA items
         if (this.progress.supplies === null || this.progress.supplies === 100) {
            priorityActionItemsCopy = priorityActionItemsCopy.filter(
               (item) => item.action !== 'SUPPLIES_DATA'
            );
         }

         // If cloud is null or 100, remove CLOUD_DATA items
         if (this.progress.cloud === null || this.progress.cloud === 100) {
            priorityActionItemsCopy = priorityActionItemsCopy.filter(
               (item) => item.action !== 'CLOUD_DATA'
            );
         }
         return priorityActionItemsCopy;
      },

      nextAction() {
         const activeStudyActions = this.attentionItems.filter(
            (action) => Number(action.studyId) === Number(this.activeStudyId)
         );
         if (activeStudyActions.length === 0) {
            return null;
         } else {
            return activeStudyActions[0];
         }
      },

      suppliesComplete() {
         return this.progress.supplies === 100;
      },

      projectComplete() {
         return this.progress.project === 100;
      },

      companyComplete() {
         return this.progress.company === 100;
      },

      cloudComplete() {
         return this.progress.cloud === 100;
      },
   },

   methods: {
      toAction(route, studyId) {
         if (!route.params) {
            route.params = {activeStudyId: studyId};
         } else {
            route.params.activeStudyId = studyId;
         }
         return route;
      },

      async loadData() {
         this.loaded = false;
         const requests = [
            this.$store.dispatch('progress/loadProgress', {
               companyId: this.$companyId,
            }),
            this.$store.dispatch('companies/loadCompany', {
               companyId: this.$companyId,
            }),
         ];
         await this.loadPage(requests);
         this.loaded = true;
      },
   },

   async mounted() {
      await this.loadData();
   },

   watch: {
      async activeStudyId() {
         if (this.isActiveStudyInSetupStage) {
            this.$router.push({name: 'study-setup'});
         } else {
            await this.loadData();
         }
      },
   },
};
</script>

<style lang="scss" scoped>
.client-dashboard {
   background: #f4f4f4;
   padding: 0 1rem 0 4rem;

   @media (max-width: 1240px) {
      margin-right: 3.125rem;
   }

   @media (max-width: 992px) {
      max-width: 760px;
      margin-right: 1rem;
   }

   .progress-section-wrapper .sme-dashboard-card {
      border-radius: 0;
   }

   .progress-section-wrapper .sme-dashboard-card:first-child {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
   }

   .progress-section-wrapper .sme-dashboard-card:last-child {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
   }
}

.progress-card-wrapper {
   @media (max-width: 1240px) {
      padding-bottom: 1.25rem;
   }
}

.section-title {
   font-size: 1.25rem;
   font-weight: 700;
   margin-bottom: 1.375rem;
   margin-left: 0.625rem;
}

.card-body {
   padding: 0 !important;
   margin: 1.5rem;
   display: flex;
   flex-direction: column;
   justify-content: center;
}

.attention-list-wrapper {
   overflow: auto;
}

.attention-list-row {
   width: 100%;
   padding: 0.25rem 0;
   border-bottom: 1px solid #e7ebf1;
   min-height: 48px;
}

.attention-list-row:last-child {
   border-bottom: 0;
}

.attention-list-icon-with-text {
   flex: 0.97;
   gap: 0.25rem;
}

.info-card {
   background-color: $secondary;
   color: $white;
   border: 1px solid #e6f1fb;
   border-radius: 10px;
   max-height: 18.75rem;

   @include md {
      height: 100%;
   }
}

.attention-text {
   font-size: 0.8125rem;
   color: $black;
   max-height: 2.4735rem;
   overflow: hidden;
}

.continue-section-subtitle {
   font-size: 0.8125rem;
   flex: 1;
}

.continue-button {
   font-size: 0.75rem;
}

.continue-section-wrapper {
   gap: 0.75rem;
}

.continue-section-title {
   font-size: 0.875rem;
   font-weight: 600;
}

.progress-percentage-study {
   font-size: 0.8125rem;
}

.progress-percentage {
   font-size: 0.875rem;
}

.card-title {
   font-size: 1.125rem;
}

.study-prog-container {
   display: flex;
   flex-direction: column;
   align-items: flex-end;
   margin-top: 1rem;
   max-height: 110px;
   overflow-y: auto;

   .study-prog {
      display: flex;

      .progress {
         width: 12rem;
      }
   }
}

::v-deep .card-body {
   justify-content: flex-start;
}

.no-action-items {
   font-size: 1rem;
   margin-bottom: 0 !important;
   color: $gray-800;
}

.column-gap {
   column-gap: 1rem;
}
</style>
